/**
 * The infrastructure half of the admin surface: agent keys, network rules (D7),
 * the `/health` read behind the go-live checklist (U4) and — since the India
 * go-live work — the PER-COUNTRY readiness read that answers the question
 * `/health` cannot: *can THIS country issue a document today?*
 *
 * ⚠ THE TWO READINESS READS ARE NOT INTERCHANGEABLE AND NEITHER SUBSUMES THE
 * OTHER. `/health` is group-wide, unauthenticated and counts flagged product
 * names; `/countries/:cc/readiness` is one country, authenticated, and judges the
 * things that actually refuse a document. They disagree about India today, which
 * is why both are read and both are rendered.
 *
 * ⚠ THE GO-LIVE SECTION CAME BACK BY HALVES, AND THE HALVES ARE THE POINT
 * (owner ruling 2026-08-14, reversing G2's web half: *"I removed it and that
 * was wrong"*).
 *
 *   · RESTORED — the `/health` transcription and `fetchHealth`. The route is
 *     `@Public()`, has always shipped, and is the group-wide gate itself. The
 *     transcription is written against the CURRENT payload, not resurrected
 *     from git: `verify_before_prod` is one counter now (`products`), and
 *     `version` did not exist when this file last carried a health DTO.
 *   · STILL RETIRED — `VerifyFlagsReport` and `fetchVerifyFlags`. `GET
 *     /admin/verify-flags` is retired SERVER-side (OD-5) and stays retired:
 *     `catalogue-pricing.e2e-spec.ts` (7) measures that the shipped
 *     `GET /products` already carries `verify_before_prod` on every row and
 *     that a group admin's flagged subset IS this counter — so the screen
 *     needs no endpoint back, and `admin.e2e-spec.ts` still pins the 404.
 *
 * ===========================================================================
 * WHY THIS IS `infra-api.ts` AND NOT `api.ts`
 * ===========================================================================
 * Phase 2.5b's web build is split between two work streams: users/roles/avatars
 * in one, this file's surfaces in the other. Both land in
 * `src/features/admin/`, so the generic name `api.ts` is deliberately left free —
 * two streams writing the same filename in the same week is a merge conflict by
 * appointment. One owner per file (the B4 precedent, `features/inquiries/api.ts`
 * header).
 *
 * Every DTO below is TRANSCRIBED from the API's own service files, cited per
 * shape, and carries only the fields a screen actually renders — a field this
 * module claims to know but never reads is a field that will be wrong the first
 * time somebody trusts it (the `PartyDto` rule).
 *
 * Every call goes through `apiRequest`, which owns the single-flight refresh
 * mutex (D-web-1). ESLint forbids `fetch` outside `src/lib/api.ts` for exactly
 * this reason.
 */

import { apiRequest, fetchBinary, type BinaryResponse } from '../../lib/api';
import type { CountryCode } from '../../domain/countries';

/* ────────────────────────────────────────────────────────────── agent keys ── */

/**
 * `AgentKeyDto` — transcribed from
 * `apps/api/src/modules/countries/agent-keys.service.ts:53`.
 *
 * `fingerprint` is the first 8 hex characters of the key's sha256 — an
 * identifier, never a credential. The raw key is NOT here: it exists only in
 * {@link CreatedAgentKeyDto}, once.
 */
export interface AgentKeyDto {
  id: string;
  country_id: number;
  label: string;
  fingerprint: string;
  last_seen_at: string | null;
  revoked_at: string | null;
  revoked: boolean;
}

/**
 * The POST response — the ONLY time the raw key crosses the wire
 * (`CreatedAgentKeyDto`, same file, line 64). `key_shown_once: true` is the
 * API stating that contract in the payload itself.
 */
export interface CreatedAgentKeyDto extends AgentKeyDto {
  key: string;
  key_shown_once: true;
}

/** `label` is `VARCHAR(60)` per `CreateAgentKeyDto` (`dto/config.dto.ts:96`). */
export const AGENT_KEY_LABEL_MAX = 60;

/**
 * The ACTIVE country's keys only (`?country=`), not every country the caller
 * manages: the country is in the URL by design decision (`routes.tsx` header),
 * so the screen for `/countries/CD/...` must not quietly list IN's keys too.
 */
export async function fetchAgentKeys(
  countryCode: string,
  includeRevoked: boolean,
  signal?: AbortSignal,
): Promise<{ data: AgentKeyDto[] }> {
  return apiRequest<{ data: AgentKeyDto[] }>('/agent-keys', {
    query: { country: countryCode, includeRevoked: includeRevoked ? true : undefined },
    signal,
  });
}

export async function createAgentKey(
  countryCode: string,
  label: string,
): Promise<CreatedAgentKeyDto> {
  return apiRequest<CreatedAgentKeyDto>('/agent-keys', {
    method: 'POST',
    body: { country: countryCode, label },
  });
}

/** Revoke = `DELETE`. The row survives as the audit record; re-revoking is 409. */
export async function revokeAgentKey(id: string): Promise<AgentKeyDto> {
  return apiRequest<AgentKeyDto>(`/agent-keys/${id}`, { method: 'DELETE' });
}

/* ─────────────────────────────────────────────────────── network rules (D7) ── */

/**
 * `NetworkRuleDto` — transcribed from
 * `apps/api/src/modules/countries/network-rules.service.ts:42`.
 */
export interface NetworkRuleDto {
  id: string;
  country_id: number;
  cidr: string;
  label: string;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The list envelope carries `client_ip` — the caller's address AS THE PREMISES
 * GUARD WOULD RESOLVE IT (same helper, same TRUST_PROXY semantics;
 * `network-rules.controller.ts` header). It exists for the "Your current IP —
 * add this network" affordance: arming an office is safe only if the address
 * the admin registers is the address the guard will actually test, so the API
 * states it rather than letting the browser guess.
 */
export interface NetworkRulesEnvelope {
  data: NetworkRuleDto[];
  client_ip: string | null;
}

/** `CreateNetworkRuleDto` limits (`dto/network-rules.dto.ts`). */
export const NETWORK_RULE_CIDR_MAX = 64;
export const NETWORK_RULE_LABEL_MAX = 120;

export async function fetchNetworkRules(
  countryCode: string,
  signal?: AbortSignal,
): Promise<NetworkRulesEnvelope> {
  return apiRequest<NetworkRulesEnvelope>(`/countries/${countryCode}/network-rules`, { signal });
}

/**
 * Invalid CIDR is a 422 `VALIDATION_FAILED` with `{field:'cidr', reason}` in
 * `details` — raised at write time by ipaddr.js on the server (D7.6). The form
 * surfaces `reason` under the field rather than a generic sentence.
 */
export async function createNetworkRule(
  countryCode: string,
  body: { cidr: string; label: string },
): Promise<NetworkRuleDto> {
  return apiRequest<NetworkRuleDto>(`/countries/${countryCode}/network-rules`, {
    method: 'POST',
    body,
  });
}

/** DELETE = deactivate. The row stays; already-inactive is 409 (agent-key precedent). */
export async function deactivateNetworkRule(
  countryCode: string,
  id: string,
): Promise<NetworkRuleDto> {
  return apiRequest<NetworkRuleDto>(`/countries/${countryCode}/network-rules/${id}`, {
    method: 'DELETE',
  });
}

/* ──────────────────────────────────────────────── /health — the go-live gate ── */

/**
 * The `verify_before_prod` block — ONE counter since the tax-groups reshape (G2).
 *
 * ⚠ TRANSCRIBED FROM TODAY'S `health.service.ts`, NOT FROM THE 2026-08-10 COPY THIS
 * FILE USED TO CARRY. That one declared five counters; four are retired and are
 * ABSENT as keys (`admin.e2e-spec.ts` pins the absence — a zero would claim they were
 * measured). Restoring the old interface would have type-checked perfectly and made
 * the screen read `undefined` as "nothing pending" for four questions nobody asks.
 *
 * `products` counts `products.verify_before_prod = 1` GLOBALLY — every country, no
 * filter — and it is the one flag still backed by a refusal (`NAME_UNVERIFIED` on
 * send/confirm/pdf, OD-4). The nested `go_live_ready` answers only "has every product
 * name been reviewed"; the TOP-LEVEL field is the actual verdict.
 */
export interface HealthVerifyCounts {
  products: number;
  /** Equal to `products` today; the shape survives a future second counter. */
  total: number;
  go_live_ready: boolean;
}

/**
 * OQ-FTS-DOCID — can party search still find customers created today?
 *
 * `blind` is a live product fault AND half the top-level gate. `unmeasurable` is NOT a
 * failure: on a new database there is nothing to check against, and the screen must say
 * so rather than show a tick nobody earned.
 */
export interface SearchIndexHealthDto {
  status: 'ok' | 'blind' | 'unmeasurable';
  sampled: number;
  measured: number;
  blind: number;
  skipped: number;
  detail: string;
}

/**
 * How many notification rules are SWITCHED ON, which are off, and where this database's
 * routing table disagrees with the matrix this build declares.
 *
 * ⚠ NOT "which notifications will reach somebody" — the server counts ROWS. Read
 * `readable` BEFORE any count: false means every number is zero because nothing was
 * measured, not because the table is empty.
 */
export interface NotificationRulesHealthDto {
  readable: boolean;
  enabled: number;
  disabled: number;
  /** `event_key · recipient · channel` — a ROLE or a reserved class, never a person. */
  disabled_rules: string[];
  /** Declared by this build, absent from this database — never deployed. */
  undeployed_rules: string[];
  /** Held by this database, no longer declared — retired, still routing. */
  retired_rules: string[];
  detail: string;
}

/**
 * WHICH COMMIT THE PROCESS THAT ANSWERED IS RUNNING (U3) — `commit` is the literal
 * `'unknown'` when no record was readable, never omitted and never guessed. Read
 * `source` before believing `commit`, the role `notification_rules.readable` plays.
 */
export interface DeployedVersionDto {
  commit: string;
  source: 'deployed_commit_file' | 'unknown';
  detail: string;
}

export interface HealthReportDto {
  status: 'ok' | 'degraded';
  version: DeployedVersionDto;
  /** NULL — never zeros — when the database could not be read. */
  verify_before_prod: HealthVerifyCounts | null;
  search_index: SearchIndexHealthDto;
  notification_rules: NotificationRulesHealthDto;
  /**
   * THE GATE, and NOT the same field as `verify_before_prod.go_live_ready`: this one is
   * that AND a search index that is not blind AND every country able to issue a document.
   * Read, never recomputed — two clients ANDing the same conditions eventually disagree,
   * and the one that forgets a condition is how a gate quietly stops gating.
   *
   * ⚠ RULE-5 CALLOUT (India go-live, cluster A5; owner Forks 11 + 12) — THE THIRD TERM IS
   * NEW, AND THIS CLIENT COULD NOT RECOMPUTE THE GATE EVEN IF IT WANTED TO. The sentence
   * read *"this one is that AND a search index that is not blind"*, a two-term gate that
   * measured green while every country carried a placeholder `countries.address_block` and
   * `POST /quotes/:id/pdf` refused all six. The third term is deliberately INVISIBLE on this
   * payload: Fork 11 keeps the unauthenticated `/health` free of anything per-country — no
   * codes, not even a bare count. So "read, never recomputed" stopped being a convention and
   * became the only option available, which is a stronger place for it to be.
   *
   * When this is `false` while `verify_before_prod.go_live_ready` is true and `search_index`
   * is `ok`, a country cannot issue a document. `CountryReadinessDto` below is where the
   * screen finds out which, per country, behind `config.manage`.
   */
  go_live_ready: boolean;
}

/**
 * `/health` — unauthenticated, and asked for anonymously on purpose.
 *
 * The route needs no bearer, and its 503 when the database is unreachable must not be
 * mistaken for a stale token and burn a refresh rotation.
 */
export async function fetchHealth(signal?: AbortSignal): Promise<HealthReportDto> {
  return apiRequest<HealthReportDto>('/health', { signal, anonymous: true });
}

/* ──────────────────────────────── /countries/:cc/readiness — the OTHER counter ── */

/**
 * The six things a country is judged on — COPIED from `READINESS_ITEM_KEYS` in
 * `apps/api/src/modules/countries/readiness.ts`, in that file's order.
 *
 * ⚠ A COPY CAN DRIFT, SO IT IS PINNED. `golive.test.tsx` reads the API source and asserts
 * this array equals the server's, in order — the `INQUIRY_STAGES` / `domain/stages.test.ts`
 * precedent, for the same reason: the browser bundle must not import a NestJS module to
 * compare six strings, and a copied vocabulary with nothing checking it is a vocabulary
 * that will be wrong.
 *
 * ⚠ AND THE SCREEN STILL RENDERS A KEY IT HAS NO WORD FOR, by its raw name. The pin fails
 * the build; it does not protect a deployed browser that is one release behind the server.
 * An item nobody worded is still an item holding a country shut, and dropping it would be
 * the silent under-reporting `flaggedNames` refuses to do one card up.
 */
export const READINESS_ITEM_KEYS = [
  'letterhead',
  'default_tax_group',
  'product_names',
  'tax_registration',
  'home_state_code',
  'tally_config',
  // U0 (`docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3, owner "go" 2026-09-25): the SAF-T(AO)
  // header — REPORTED, applicable only where `countries.saft_ao` is set.
  'saft_header',
] as const;

export type ReadinessItemKey = (typeof READINESS_ITEM_KEYS)[number];

/**
 * One item's verdict — `dto/readiness.dto.ts:33-53`, snake_case on the wire.
 *
 * `defect` is a WORD, not a sentence: the server's vocabulary is
 * `'unconfirmed' | 'multiline' | 'missing' | 'flagged' | 'unset' | 'unverified'` and it is
 * DERIVED there from the letterhead predicate's own type, so it widens without warning.
 * It is typed `string | null` here for that reason — a narrower copy would make the build
 * fail on a defect word the operator still needs to read.
 *
 * `refuses_at` names the route, the source site and the error code that refuses on this
 * item today, and is `null` for a REPORTED item. That null is a claim: nothing refuses on
 * this. The screen renders the two halves apart because of it.
 */
export interface ReadinessItemDto {
  key: ReadinessItemKey;
  /** True iff a shipped path refuses on this today — the membership rule. */
  gating: boolean;
  satisfied: boolean;
  /**
   * FALSE iff this country has no such thing to supply — owner ruling N-56, 2026-09-19.
   *
   * ⚠ AN INAPPLICABLE ITEM ARRIVES `satisfied: true`. The server's argument for that is at
   * `readiness.ts`, `ReadinessItemVerdict.applicable`, and is not restated here — what
   * matters on this side is that a screen keying its sentence off `satisfied` alone would
   * tell a Congolese operator their tax registration number had been supplied.
   */
  applicable: boolean;
  /** The column or table an operator has to fix. */
  field: string;
  /** `null` iff satisfied. */
  defect: string | null;
  /** The shipped refusal (route, site, error code), or `null` for a reported item. */
  refuses_at: string | null;
  /** The measured quantity behind the verdict, when there is one. */
  observed: number | null;
}

/**
 * ONE COUNTRY'S ANSWER — `dto/readiness.dto.ts:55-67`.
 *
 * ⚠ `can_issue_documents` IS THE ANSWER, AND IT IS NOT `/health`'s. `/health.go_live_ready`
 * is one group-wide boolean over flagged product NAMES and the search probe; this is one
 * country over the things that actually refuse a document. MEASURED on `crf_v3_ta`: India
 * has ZERO flagged names — the per-country flag count reports it READY — while
 * `letterheadDefect` on India's stored row returns `countries.address_block: unconfirmed`,
 * so `POST /quotes/:id/pdf` refuses and India cannot produce a quotation at all. Two
 * counters, one database, opposite answers about the same country. The screen shows BOTH
 * and never lets one stand in for the other.
 */
export interface CountryReadinessDto {
  country: CountryCode;
  /** EVERY GATING item satisfied. A reported item may be unsatisfied while this is true. */
  can_issue_documents: boolean;
  gating_unsatisfied: number;
  reported_unsatisfied: number;
  items: ReadinessItemDto[];
}

/**
 * `/countries/:cc/readiness` — AUTHENTICATED and COUNTRY-SCOPED, unlike `/health`.
 *
 * ⚠ NOT `anonymous: true`, and the difference is the owner's Fork 11: the unauthenticated
 * `/health` discloses NOTHING per country — no codes, not even a bare count — and its wire
 * shape gains no key, no country code and no integer. Everything this route answers with
 * names one country's columns, so it goes through the bearer like every other admin read
 * and its 401 is a real 401 worth a refresh.
 */
export async function fetchCountryReadiness(
  countryCode: string,
  signal?: AbortSignal,
): Promise<CountryReadinessDto> {
  return apiRequest<CountryReadinessDto>(`/countries/${countryCode}/readiness`, { signal });
}

/* ──────────────────────────────── /countries/:cc — the letterhead write surface ── */

/**
 * The country row, as `GET /countries/:cc` answers it — TRANSCRIBED from `CountryDto` in
 * `apps/api/src/modules/countries/countries.service.ts`.
 *
 * ⚠ ONLY THE FIELDS `CountrySettingsPage` RENDERS ARE DECLARED, which is the `PartyDto`
 * rule this file's header states: the wire carries a dozen more (`currency`,
 * `fy_start_month`, `tz`, `two_step_signoff`, `default_deployment_assignee_id` …) and a
 * field declared here but never read is a field that will be wrong the first time somebody
 * trusts it. `name` is in because the screen says the country's name back to the operator;
 * `tax_label` is in because it is the LABEL the GSTIN/TRN/VAT field must wear — the number
 * an Indian operator types is a GSTIN and a Tanzanian one a VAT No., and the server already
 * knows which, so the screen asks rather than hard-coding a table of six.
 */
export interface CountryDto {
  code: CountryCode;
  name: string;
  legal_entity: string;
  address_block: string;
  /** `'GSTIN'` · `'TRN'` · `'VAT No.'` · `'NIF'` — the label printed beside the number. */
  tax_label: string;
  /** NULL for all six countries as measured on 14 Aug 2026 — nothing has been supplied. */
  tax_registration: string | null;
  /**
   * The country's OWN state — the pair §9.2 step 4 compares the customer's resolved state
   * against to pick CGST+SGST versus IGST. Declared here since the owner ruling that put
   * the pair on this screen (2026-08-26: *"Copy that points a coordinator at a page where
   * the field does not exist is a dead end"* — the C2 hint in `QuoteBuilderPage` sends
   * people to Country settings, and this is the field it sends them to). MEASURED on the
   * seed: `'24'` / `'Gujarat'` for IN, NULL for the other five — and NULL is LEGAL for a
   * NONE-rule country, so an empty pair here is a fact, not a defect.
   */
  home_state_code: string | null;
  home_state_name: string | null;
  /**
   * THE SAF-T(AO) HEADER — migration 67, `docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3 U0
   * (owner "go" 2026-09-25). `saft_ao` is the fiscal-regime flag and is READ ONLY on this
   * side: the screen keys the "Fiscal identity (AGT)" section on it, and `CountryPatchBody`
   * carries no such field because the route's whitelist refuses it (400). The other six
   * are the section's inputs; every one is `null` until the owner supplies it, and NULL is
   * how "not supplied" is spelled — the `tax_registration` rule.
   */
  saft_ao: boolean;
  agt_validation_no: string | null;
  commercial_registry: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  cae: string | null;
}

/**
 * The lengths the ROUTE will judge, copied from `UpdateCountryDto`
 * (`apps/api/src/modules/countries/dto/countries.dto.ts`).
 *
 * ⚠ A FORM THAT LET SOMEBODY TYPE PAST THESE AND THEN SHOWED THEM A 422 WOULD BE THE
 * SCREEN-OFFERS-WHAT-THE-ROUTE-REFUSES DEFECT, one level down from the letterhead gate
 * itself. `country-settings.test.tsx` asserts each `maxLength` on the rendered input, so a
 * change on the server that is not mirrored here fails a test rather than a user.
 */
export const COUNTRY_LEGAL_ENTITY_MAX = 160;
export const COUNTRY_ADDRESS_MAX = 2000;
export const COUNTRY_TAX_REGISTRATION_MAX = 40;
/**
 * `home_state_code` is `@Matches(/^\d{2}$/)` — exactly two characters, so the cap IS the
 * format's length. The DIGITS-ONLY half of the rule is deliberately NOT mirrored as an
 * input filter: the page's precedent (the letterhead predicate) leaves content rules to
 * the server and surfaces the refusal under the field, and a client that silently ate
 * keystrokes would be a second, invisible validator.
 */
export const COUNTRY_HOME_STATE_CODE_MAX = 2;
/** `@MaxLength(60)` on `home_state_name` (`dto/countries.dto.ts`). */
export const COUNTRY_HOME_STATE_NAME_MAX = 60;
/*
 * The SAF-T(AO) header (U0) — `@MaxLength` on the DTO for the four free-text columns, and
 * the FORMAT's length for the two patterned ones: `agt_validation_no` is `\d+/AGT/\d{4}`
 * under a `@MaxLength(20)`, `cae` is `@Matches(/^\d{5}$/)` so five IS the cap. As with the
 * state code, the pattern half is NOT mirrored as an input filter — the server's 400 lands
 * under the field (`fieldOfRefusal`), never a client that silently eats keystrokes.
 */
export const COUNTRY_AGT_VALIDATION_NO_MAX = 20;
export const COUNTRY_COMMERCIAL_REGISTRY_MAX = 50;
export const COUNTRY_CITY_MAX = 50;
export const COUNTRY_PROVINCE_MAX = 50;
export const COUNTRY_POSTAL_CODE_MAX = 10;
export const COUNTRY_CAE_MAX = 5;

/** `config.manage` IN THIS COUNTRY (D3) — the same gate the other infra reads carry. */
export async function fetchCountry(
  countryCode: string,
  signal?: AbortSignal,
): Promise<CountryDto> {
  return apiRequest<CountryDto>(`/countries/${countryCode}`, { signal });
}

/**
 * `PATCH /countries/:cc` — the write.
 *
 * ⚠ THE BODY IS A DIFF, NOT A SNAPSHOT, AND THAT IS A CORRECTNESS REQUIREMENT RATHER THAN A
 * SAVING. The route's letterhead gate judges THE FIELDS THE REQUEST SUPPLIES
 * (`countries.service.ts` `suppliedLetterheadDefect`). MEASURED on `crf_v3_ta`: all six
 * countries still hold a placeholder `address_block`, so a screen that PATCHed every field
 * on every save would drag that placeholder into every request and 422 an operator who was
 * only correcting the GSTIN. `CountrySettingsPage` therefore sends only what changed, and
 * `country-settings.test.tsx` pins the request body for exactly that case.
 */
/**
 * ⚠ `tax_registration` IS `string | null` HERE AND THE LETTERHEAD PAIR IS NOT, WHICH
 * MIRRORS THE DTO EXACTLY. `UpdateCountryDto` declares `tax_registration?: string | null` —
 * the column is nullable and NULL is how "not supplied yet" is spelled — while
 * `legal_entity` and `address_block` are `NOT NULL` and take no null. A single
 * `string | null` for all of them would type-check a request the server answers 422 to.
 *
 * `home_state_code` / `home_state_name` are nullable for the same reason `tax_registration`
 * is — five of six countries legally hold NULL — with one refusal the letterhead trio does
 * not have: the route REFUSES `home_state_code: null` while an ACTIVE `IN_GST_STATE_SPLIT`
 * group exists (`countries.service.ts` `assertStateConsistency`, 422 `VALIDATION_FAILED`
 * with `reason: 'SPLIT_GROUP_NEEDS_HOME_STATE'` and `field: 'home_state_code'` — measured
 * by `country-home-state-write.e2e-spec.ts`). The screen cannot know from this module's
 * reads whether such a group exists — the country row does not carry it — so that refusal
 * is surfaced VERBATIM under the field rather than predicted (`country-settings-model.ts`
 * `fieldOfRefusal`).
 */
export interface CountryPatchBody {
  legal_entity?: string;
  address_block?: string;
  tax_registration?: string | null;
  home_state_code?: string | null;
  home_state_name?: string | null;
  /*
   * The SAF-T(AO) header (U0) — nullable for the `tax_registration` reason, and each takes
   * the same `'' → null` translation in `changedFields`. `saft_ao` is deliberately NOT a
   * member: the route's whitelist answers 400 to it, and a body type that admitted it would
   * type-check a request the server refuses.
   */
  agt_validation_no?: string | null;
  commercial_registry?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  cae?: string | null;
}

export async function updateCountry(
  countryCode: string,
  body: CountryPatchBody,
): Promise<CountryDto> {
  return apiRequest<CountryDto>(`/countries/${countryCode}`, { method: 'PATCH', body });
}

/**
 * `POST /countries/:cc/saft-ao/export` — design §3 U6: the SAF-T (AO) file for a period, as
 * bytes (`config.manage` + `amounts.view`). The body is the period and nothing else (10 c)).
 */
export async function exportSaftAo(
  countryCode: string,
  period: { from: string; to: string },
): Promise<BinaryResponse> {
  return fetchBinary(`/countries/${countryCode}/saft-ao/export`, period);
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const adminInfraKeys = {
  agentKeys: (cc: string, includeRevoked: boolean) =>
    ['admin', 'agent-keys', cc, { includeRevoked }] as const,
  networkRules: (cc: string) => ['admin', 'network-rules', cc] as const,
  holidays: (cc: string, year: number) => ['admin', 'holidays', cc, year] as const,
  /** Unscoped: a person's calendar gap is not a property of the country on screen. */
  calendarGaps: () => ['admin', 'calendar-gaps'] as const,
  /** Unscoped, like the route: `/health` is one group-wide answer, not a per-country one. */
  health: () => ['health'] as const,
  /**
   * SCOPED, AND A SEPARATE KEY FROM {@link adminInfraKeys.health} ON PURPOSE.
   *
   * Two different questions with two different answers must not share a cache entry: one
   * is group-wide and unauthenticated, the other is one country's and behind
   * `config.manage` there. Keying readiness under `health()` would let a country seat's
   * verdict be served to the next country the user switches to — the go-live screen
   * remounts on every `/countries/:cc/...` navigation, so that is not hypothetical.
   */
  countryReadiness: (cc: string) => ['admin', 'readiness', cc] as const,
  /**
   * The country ROW — a third key again, beside `health()` and `countryReadiness(cc)`.
   *
   * It is the row the operator EDITS; readiness is the VERDICT over it. A save must
   * invalidate both, and one key could not express "the thing changed, so re-ask the judge"
   * — `CountrySettingsPage` invalidates `countryReadiness(cc)` on every successful PATCH
   * for that reason, which is what makes the go-live screen agree with the form.
   */
  country: (cc: string) => ['admin', 'country', cc] as const,
};

/* ── the working calendar: public holidays, and who has no calendar ──────── */

export interface HolidayDto {
  date: string;
  name: string;
}

export interface HolidayCalendarDto {
  country_code: string;
  /**
   * The calendar this country actually WRITES. Its own code unless it borrows
   * another — so an edit here may reach more countries than the one on screen.
   */
  calendar_key: string;
  /** Other countries reading the same calendar. Non-empty = a wider blast radius. */
  shared_with: string[];
  year: number | null;
  holidays: HolidayDto[];
}

export interface CalendarGapDto {
  user_id: string;
  name: string;
  email: string;
  is_group_admin: boolean;
  /** Held role countries — context for choosing the primary, not the cause.
   * RULING 5 CALLOUT: `reason` retired with migration 58; the gap has ONE
   * cause now (no primary country and no complete override). */
  countries: string[];
}

export async function fetchHolidays(
  countryCode: string,
  year: number,
  signal?: AbortSignal,
): Promise<HolidayCalendarDto> {
  return apiRequest<HolidayCalendarDto>(
    `/countries/${countryCode}/holidays?year=${String(year)}`,
    { signal },
  );
}

/** Replaces the whole year — see the route's own note on why not a merge. */
export async function replaceHolidays(
  countryCode: string,
  year: number,
  holidays: HolidayDto[],
): Promise<HolidayCalendarDto> {
  return apiRequest<HolidayCalendarDto>(`/countries/${countryCode}/holidays`, {
    method: 'PUT',
    body: { year, holidays },
  });
}

export async function fetchCalendarGaps(signal?: AbortSignal): Promise<CalendarGapDto[]> {
  return apiRequest<CalendarGapDto[]>('/users/calendar-gaps', { signal });
}

export async function setUserCalendar(
  userId: string,
  body: {
    working_days?: string[] | null;
    holiday_calendar_key?: string | null;
    /** Ruling 5 — the first-class fix: a country CODE, or null to clear. */
    primary_country?: string | null;
  },
): Promise<unknown> {
  return apiRequest<unknown>(`/users/${userId}/calendar`, { method: 'PATCH', body });
}

/* ═══════════════════════════ v22-E3 · the payment split ══════════════════ */

export interface PaymentSplitDto {
  country_code: string;
  /** "20.00" or null — null is NO SPLIT, the country behaves as before. */
  advance_pct: string | null;
  /** Derived server-side: 100 − advance. */
  balance_pct: string | null;
}

/** grp-only on the server (`config.global`); the page hides the card for others. */
export async function fetchPaymentSplit(
  countryCode: string,
  signal?: AbortSignal,
): Promise<PaymentSplitDto> {
  return apiRequest<PaymentSplitDto>(`/countries/${countryCode}/payment-split`, { signal });
}

export async function updatePaymentSplit(
  countryCode: string,
  advancePct: number | null,
): Promise<PaymentSplitDto> {
  return apiRequest<PaymentSplitDto>(`/countries/${countryCode}/payment-split`, {
    method: 'PUT',
    body: { advance_pct: advancePct },
  });
}

